/* eslint-disable max-lines-per-function */
import { Form, Formik } from "formik";
import React from "react";
import { Grid } from "@material-ui/core";
import * as y from "yup";
import { SelectOrganization } from "components/haven-design/form/special-fields";
import { HistoryBackButton } from "components/buttons";
import { Checkbox, SubmitButton } from "components/haven-design/form";
import { InputWithErrorLabel as Input } from "components/haven-design/form/inputs";
import { MaterialFormikSelectAutocomplete } from "components/haven-design/form/select";
import { extendedStatesList } from "components/haven-design/state-picker";
import { Modal, Button } from "components/haven-design";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import styles from "../adminDashboard.module.css";

interface Address {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface InterestList {
  address?: Address;
  email?: string;
  hasPaperDisclaimer?: boolean;
  isArchived?: boolean;
  name?: string;
  organizationId?: string;
  phone?: string;
  type?: string;
}

interface Props {
  listId?: string;
  initialData: InterestList;
  formType: "update" | "new";
  handleSubmit: (values:InterestList) => void | Promise<any>;
  sending: boolean;
}

export const formSchema = y.object().shape({
  __typename: y.string().strip(true),

  id: y.string().strip(true),

  isArchived: y.boolean(),

  organizationId: y
    .string()
    .trim()
    .required(),

  name: y
    .string()
    .trim()
    .max(40)
    .required(),

  email: y
    .string()
    .trim()
    .nullable(),

  phone: y
    .string()
    .trim()
    .nullable(),

  hasPaperDisclaimer: y.boolean(),

  address: y.object().shape({
    __typename: y.string().strip(true),

    name: y
      .string()
      .trim()
      .max(40)
      .required(),

    address1: y
      .string()
      .trim()
      .required(),

    address2: y
      .string()
      .nullable()
      .trim(),

    city: y
      .string()
      .trim()
      .required(),

    state: y
      .string()
      .trim()
      .required(),

    zip: y
      .string()
      .trim()
      .matches(/^\d{5}(?:[-\s]\d{4})?$/, "Incorrect zipcode format")
      .required(),
  }),
});


const ArchiveConfirmationModal: React.FC<{
  isOpen: boolean,
  onClose: () => void,
  onOK: () => void
}> = ({ isOpen, onClose, onOK }) => (
  <Modal
    isOpen={isOpen}
    title="Confirm Archive"
    onRequestClose={onClose}
  >
    <DialogContent>
        Are you sure you want to archive this interest list?
        This will update all active applications&apos; status to Removed.
    </DialogContent>

    <DialogActions>
      <Button
        size="L"
        color="secondary"
        marginRight
        onClick={onClose}
      >
          Cancel
      </Button>
      <Button
        size="L"
        color="primary"
        onClick={() => {
          onClose();
          onOK();
        }}
      >
          Submit and Archive
      </Button>
    </DialogActions>
  </Modal>
);

const ListsForm: React.FC<Props> = ({ handleSubmit, formType, initialData }) => {
  const [openArchive, setOpenArchive] = React.useState(false);

  const formSubmit = (values:InterestList) => {
    if (formType === "update" && !initialData.isArchived && values.isArchived) {
      setOpenArchive(true);
    } else {
      handleSubmit(values);
    }
  };

  return (
    <div className={`${styles.formWidth}`}>

      <Formik
        initialValues={initialData}
        onSubmit={formSubmit}
        validationSchema={formSchema}>
        {({ isSubmitting, values }) => (
          <>
            <Form>
              <Grid container className={`card ${styles.formBottom}`}>
                <Grid item container spacing={2} xs={12} sm={8} md={6}>
                  <Grid item xs={12}>
                    {formType === "update" && (
                      <Checkbox label="Archive" name="isArchived" checked={!!values.isArchived} />
                    )}

                    <p>Required fields marked with *</p>
                  </Grid>
                  <Grid item xs={12}>
                    <Input margin="none" label="Name *" name="name" type="text" />
                  </Grid>

                  <Grid item xs={12}>
                    <Input margin="none" label="Property Email" name="email" type="text" />
                  </Grid>

                  <Grid item xs={12}>
                    <Input margin="none" label="Property phone" name="phone" type="text" />
                  </Grid>

                  <Grid item xs={12}>
                    <Input margin="none" label="Address Name *" name="address.name" type="text" />
                  </Grid>

                  <Grid item xs={12}>
                    <Input margin="none" label="Address *" name="address.address1" type="text" />
                  </Grid>

                  <Grid item xs={12}>
                    <Input margin="none" label="Address 2" name="address.address2" type="text" />
                  </Grid>

                  <Grid item xs={12}>
                    <Input margin="none" label="City *" name="address.city" type="text" />
                  </Grid>

                  <Grid item xs={12}>
                    <MaterialFormikSelectAutocomplete
                      id="address.state"
                      margin="none"
                      name="address.state"
                      placeholder="State *"
                      title="State *"
                      options={extendedStatesList}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Input margin="none" label="Zip *" name="address.zip" type="text" />
                  </Grid>

                  <Grid item xs={12}>
                    <SelectOrganization name="organizationId" label="Organization *" showLabel />
                  </Grid>

                  <Grid item xs={12}>
                    <Checkbox
                      label="Add Paper Application Disclaimer"
                      name="hasPaperDisclaimer"
                      checked={values.hasPaperDisclaimer}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}><br/></Grid>
                <Grid item xs={12}>
                  <HistoryBackButton marginRight size="L" />

                  <SubmitButton isWide text="Submit" loadingText="Sending" isLoading={isSubmitting} />
                </Grid>
              </Grid>
            </Form>

            <ArchiveConfirmationModal
              isOpen={openArchive}
              onClose={() => setOpenArchive(false)}
              onOK={() => handleSubmit(values)} />
          </>
        )}</Formik>
    </div>
  );
};

export default ListsForm;
